import gsap from 'gsap';
import 'element-closest';
import { ANIMATIONS, CONFIG } from '../config';

const ACTIVE_TAB = 'bfv-jump-navigation__tab--active';
const NAV_SCROLL_X_START = 'bfv-jump-navigation__nav--scroll-x-start';
const NAV_SCROLL_X_END = 'bfv-jump-navigation__nav--scroll-x-end';

export default class JumpNavigation {
  constructor(options = {}) {
    this.$root = options.element;
    this.tabs = this.$root.querySelectorAll('.js-bfv-jump-navigation__tab');
    this.tablink = this.$root.querySelectorAll('.bfv-jump-navigation__link');
    this.tabNumber = 0;
    this.$jumpNav = this.$root.querySelector('.js-bfv-jump-navigation__navigation');
    this.$tabsNavDivWrapper = this.$root.querySelector('.js-bfv-jump-navigation__ul-wrapper');
    this.$scrollArrowLeft = this.$jumpNav.querySelector('.js-bfv-jump-navigation__arrow-left');
    this.$scrollArrowRight = this.$jumpNav.querySelector('.js-bfv-jump-navigation__arrow-right');
  }

  init() {
    this.bindEvents();
    this.setCurrentTab();
  }

  bindEvents() {
    Array.prototype.slice.call(this.tablink).forEach(($tab, index) => {
      $tab.addEventListener('click', (event) => {
        this.tabNumber = index;
        this.handleTabClick(event);
      });
    });

    window.addEventListener(CONFIG.events.RESIZE, () => this.handleWindowResize());

    this.$tabsNavDivWrapper.addEventListener('scroll', (event) => {
      this.handleHorizontalScrolling(event);
    });

    this.$scrollArrowLeft.addEventListener('click', () => this.scrollToPreviousTab());
    this.$scrollArrowRight.addEventListener('click', () => this.scrollToNextTab());
  }

  setCurrentTab() {
    const $tab = this.tabNumber;
    this.tabs[$tab].classList.add(ACTIVE_TAB);
  }

  /**
   * Recomputed classes for arrows and shadows
   */
  handleWindowResize() {
    // Add scrolled to end if does not need to get scrolled
    if (this.$tabsNavDivWrapper.scrollWidth <= Math.ceil(this.$tabsNavDivWrapper.getBoundingClientRect().width)) {
      this.$jumpNav.classList.add(NAV_SCROLL_X_END);
    } else {
      this.$jumpNav.classList.remove(NAV_SCROLL_X_END);
    }
  }

  /**
   * Function to handle horizontal scroll of tab navigation and fade out overflow
   * @function
   */
  handleHorizontalScrolling(event) {
    const boundingUL = event.currentTarget.querySelector('.js-bfv-jump-navigation__ul').getBoundingClientRect();
    const boundingNav = this.$jumpNav.getBoundingClientRect();

    if (this.$tabsNavDivWrapper.scrollLeft > 10) {
      this.$jumpNav.classList.remove(NAV_SCROLL_X_START);
      this.$jumpNav.classList.remove(NAV_SCROLL_X_END);

      if (boundingUL.right <= boundingNav.right + 10) {
        if (!this.$jumpNav.classList.contains(NAV_SCROLL_X_END)) {
          this.$jumpNav.classList.add(NAV_SCROLL_X_END);
        }
      }

    } else {
      this.$jumpNav.classList.add(NAV_SCROLL_X_START);
      this.$jumpNav.classList.remove(NAV_SCROLL_X_END);
    }
  }

  handleTabClick(event) {
    const $tabNumber = this.tabNumber;
    Array.prototype.slice.call(this.tabs).forEach(($tab, index) => {
      $tab.classList.remove(ACTIVE_TAB);

      if (index === $tabNumber) {
        $tab.classList.add(ACTIVE_TAB);
      }
    });

    JumpNavigation.smoothVerticalScroll(event);
  }

  /**
   * compute position and scroll to next tab
   */
  scrollToNextTab() {
    const currentIndex = this.getCurrentLeftMostTab();
    const currentScrollLeft = this.$tabsNavDivWrapper.scrollLeft;
    // scroll to next tab
    const targetScrollLeft = Array.prototype.slice.call(this.tabs)[currentIndex + 1].offsetLeft;

    this.smoothScrollTo(currentScrollLeft, targetScrollLeft);
  }

  /**
   * compute position and scroll to previous tab
   */
  scrollToPreviousTab() {
    const currentIndex = this.getCurrentLeftMostTab(true);
    const currentScrollLeft = this.$tabsNavDivWrapper.scrollLeft;
    let targetScrollLeft = 0;

    if (currentIndex > 0) {
      if (this.isTabLeftAligned(currentIndex)) {
        // scroll to previous tab, if we are at the beginning of a tab
        targetScrollLeft = Array.prototype.slice.call(this.tabs)[currentIndex - 1].offsetLeft;
      } else {
        // scroll to beginning of current tab, if we are not there already
        targetScrollLeft = Array.prototype.slice.call(this.tabs)[currentIndex].offsetLeft;
      }
    } // if currentIndex === 0, then targetScrollLeft stays 0

    this.smoothScrollTo(currentScrollLeft, targetScrollLeft);
  }


  getCurrentLeftMostTab() {
    return Array.prototype.slice.call(this.tabs).reduce((bestIndex, $current, index) => {
      if (this.$tabsNavDivWrapper.scrollLeft >= $current.offsetLeft) {
        return index;
      }
      return bestIndex;
    }, 0);
  }

  /**
   * @param index
   * @returns {boolean} - true, if selected tab is currentLeftMostTab and fully visible
   *    This matters for scrolling to previous tab, since otherwise we only want to jump to start of current tab
   */
  isTabLeftAligned(index) {
    return Array.prototype.slice.call(this.tabs)[index].offsetLeft === this.$tabsNavDivWrapper.scrollLeft;
  }

  /**
   * @param currentScrollLeft
   * @param targetScrollLeft
   * scroll tabsNavUlWrapper smoothly to a certain position by going in several steps
   */
  smoothScrollTo(currentScrollLeft, targetScrollLeft) {
    const steps = 10;
    const distance = targetScrollLeft - currentScrollLeft;
    let slideCount = 0;

    const slideTimer = setInterval(() => {
      slideCount += 1;
      // compute from initial value to avoid rounding mistakes
      this.$tabsNavDivWrapper.scrollLeft = currentScrollLeft + (distance / steps * slideCount);

      if (slideCount === steps) {
        window.clearInterval(slideTimer);
      }
    }, ANIMATIONS.default.DURATION * 1000 / steps);
  }

  static smoothVerticalScroll(event) {
    const targetId = event.currentTarget.getAttribute('href');
    event.preventDefault();

    gsap.to(window, ANIMATIONS.default.DURATION, {
      scrollTo: {
        y: targetId,
        offsetY: 110,
      },
    });
  }
}
